/**
 * Denial Mapping Engine
 * -----------------------------------------------------------------------------
 * Translates raw ANSI / CARC / RARC codes (and free-text denial descriptions)
 * into operational denial groupings that revenue-cycle teams actually act on.
 *
 * Why this exists:
 *   Raw CARC/RARC reporting is noise. "Top denial = CO-16" is not actionable.
 *   The operational grouping ("Missing Documentation") is.
 *
 * What it provides:
 *   - GROUPINGS:        the canonical list of operational denial groupings
 *   - CODE_TO_GROUP:    a mapping of common CARC/RARC codes to groupings
 *   - GROUP_META:       per-grouping recovery likelihood + recommended action
 *   - mapCodeToGroup:   normalize a single code and return its primary group
 *   - primaryDriver:    given multiple codes/remarks on a single denial,
 *                       infer the PRIMARY operational driver
 *   - aggregateDrivers: given an array of {codes, amount} denials,
 *                       return the Top-N drivers with $ at risk, count,
 *                       recovery likelihood, trending direction, action.
 */

// -----------------------------------------------------------------------------
// 1. Operational groupings (the only thing executives should see at the top)
// -----------------------------------------------------------------------------
export const GROUPINGS = [
  "Authorization",
  "Prior Authorization",
  "Medical Necessity",
  "Eligibility",
  "Coordination of Benefits",
  "Timely Filing",
  "Duplicate Claim",
  "Billing/Coding Errors",
  "Non-Covered Services",
  "Missing Documentation",
  "Level of Care",
  "Registration Errors",
  "Invalid Modifiers",
  "Bundled Services",
  "Filing Limits",
  "Payor Policy Issues",
  "Coverage Exhaustion",
  "Single Case Agreement",
  "Patient Responsibility",
  "Other",
];

// -----------------------------------------------------------------------------
// 2. Raw code mapping (ANSI/CARC + RARC). Keys are normalized "CO-16" form.
// -----------------------------------------------------------------------------
const RAW_CODE_MAP = {
  // ---- Common CARC (Claim Adjustment Reason Codes) ----
  "CO-15":  "Authorization",
  "CO-16":  "Missing Documentation",
  "CO-18":  "Duplicate Claim",
  "CO-22":  "Coordination of Benefits",
  "CO-23":  "Coordination of Benefits",
  "CO-24":  "Coordination of Benefits",
  "CO-26":  "Eligibility",
  "CO-27":  "Eligibility",
  "CO-29":  "Timely Filing",
  "CO-31":  "Eligibility",
  "CO-39":  "Authorization",
  "CO-45":  "Payor Policy Issues",
  "CO-50":  "Medical Necessity",
  "CO-54":  "Billing/Coding Errors",
  "CO-58":  "Billing/Coding Errors",
  "CO-96":  "Non-Covered Services",
  "CO-97":  "Bundled Services",
  "CO-109": "Payor Policy Issues",
  "CO-119": "Filing Limits",
  "CO-151": "Medical Necessity",
  "CO-167": "Non-Covered Services",
  "CO-170": "Authorization",
  "CO-171": "Authorization",
  "CO-185": "Billing/Coding Errors",
  "CO-197": "Prior Authorization",
  "CO-198": "Prior Authorization",
  "CO-204": "Non-Covered Services",
  "CO-226": "Missing Documentation",
  "CO-251": "Missing Documentation",
  "CO-252": "Missing Documentation",

  // Patient responsibility (informational, surfaced separately)
  "PR-1":   "Patient Responsibility",
  "PR-2":   "Patient Responsibility",
  "PR-3":   "Patient Responsibility",
  "PR-49":  "Non-Covered Services",
  "PR-96":  "Non-Covered Services",
  "PR-119": "Filing Limits",
  "PR-204": "Non-Covered Services",

  // ---- RARC (Remittance Advice Remark Codes) ----
  "M15":   "Authorization",
  "M50":   "Missing Documentation",
  "M51":   "Billing/Coding Errors",
  "M76":   "Missing Documentation",
  "M77":   "Billing/Coding Errors",
  "M86":   "Registration Errors",
  "N4":    "Eligibility",
  "N15":   "Level of Care",
  "N18":   "Authorization",
  "N30":   "Eligibility",
  "N130":  "Missing Documentation",
  "N179":  "Missing Documentation",
  "N197":  "Prior Authorization",
  "N362":  "Billing/Coding Errors",
  "N418":  "Payor Policy Issues",
  "N428":  "Billing/Coding Errors",
  "N448":  "Non-Covered Services",
  "N522":  "Duplicate Claim",
};

// Free-text keyword fallback when a code is missing or unmapped.
const KEYWORD_MAP = [
  [/level\s*of\s*care|locus|casii|asam/i,                   "Level of Care"],
  [/concurrent\s*review|continued\s*stay/i,                 "Authorization"],
  [/prior\s*auth|pre[\s-]?auth/i,                           "Prior Authorization"],
  [/medical(ly)?\s*necess/i,                                "Medical Necessity"],
  [/coordination\s*of\s*benefit|cob\b/i,                    "Coordination of Benefits"],
  [/timely\s*filing|past\s*filing/i,                        "Timely Filing"],
  [/duplicate/i,                                            "Duplicate Claim"],
  [/missing\s*(info|document|record)/i,                     "Missing Documentation"],
  [/modifier/i,                                             "Invalid Modifiers"],
  [/bundle/i,                                               "Bundled Services"],
  [/non[\s-]?covered|not\s*covered/i,                       "Non-Covered Services"],
  [/eligib/i,                                               "Eligibility"],
  [/registration|demograph/i,                               "Registration Errors"],
  [/exhaust|benefit\s*max|max(imum)?\s*benefit/i,           "Coverage Exhaustion"],
  [/single\s*case\s*agreement|sca\b/i,                      "Single Case Agreement"],
  [/policy/i,                                               "Payor Policy Issues"],
  [/filing\s*limit/i,                                       "Filing Limits"],
  [/authorization|auth\b/i,                                 "Authorization"],
];

// -----------------------------------------------------------------------------
// 3. Group-level metadata: recovery likelihood (0-1) + recommended action.
// -----------------------------------------------------------------------------
export const GROUP_META = {
  "Authorization":            { recovery: 0.65, action: "Tighten concurrent-review documentation and capture auth numbers at admission. Bundle CO-15 / M15 appeals weekly." },
  "Prior Authorization":      { recovery: 0.62, action: "Audit the pre-auth workflow for PHP / IOP — start auths 3 days earlier and attach LOCUS / CASII scores." },
  "Medical Necessity":        { recovery: 0.55, action: "Strengthen medical-necessity letters with LOCUS / CASII scores, treatment plan excerpts, and progress notes." },
  "Eligibility":              { recovery: 0.58, action: "Move eligibility verification to T-2 (two days pre-service); flag mid-month plan changes automatically." },
  "Coordination of Benefits": { recovery: 0.60, action: "Verify primary insurance at scheduling; chase primary EOBs within 48 hours of denial." },
  "Timely Filing":            { recovery: 0.18, action: "Audit clearinghouse rejections — most CO-29s start as silent claim rejections weeks before they appear on the 835." },
  "Duplicate Claim":          { recovery: 0.30, action: "Investigate split-claim logic; check whether secondary submissions are being misread as duplicates." },
  "Billing/Coding Errors":    { recovery: 0.72, action: "Run a coding-QA pass on top 10 CPTs; rework + resubmit clean claims within 7 days." },
  "Non-Covered Services":     { recovery: 0.20, action: "Surface to payor-mix dashboard; consider contract renegotiation for high-volume CPTs trending non-covered." },
  "Missing Documentation":    { recovery: 0.70, action: "Build automated documentation requests for CO-16 / N130; aim for resubmission within 14 days." },
  "Level of Care":            { recovery: 0.52, action: "Capture LOCUS / CASII at every level-of-care change; appeal with full treatment timeline + clinical justification." },
  "Registration Errors":      { recovery: 0.68, action: "Front-end QA at intake: subscriber ID, plan, group, primary vs secondary. Most CO-22/M86 trace back here." },
  "Invalid Modifiers":        { recovery: 0.66, action: "Modifier 25 / 59 audit and bundle-aware coding QA before submission." },
  "Bundled Services":         { recovery: 0.42, action: "Review NCCI edits and consider modifier 59 with clinical documentation when distinct service is medically justified." },
  "Filing Limits":            { recovery: 0.20, action: "Surface aged claims at T-30 to filing deadline; build an exception workflow for backed-up queues." },
  "Payor Policy Issues":      { recovery: 0.38, action: "Build a payor-policy library and refresh quarterly. Track policy changes by payor + CPT." },
  "Coverage Exhaustion":      { recovery: 0.28, action: "Surface 6-week-out benefit projections; transition to SCA or appeal a benefit extension before exhaustion hits." },
  "Single Case Agreement":    { recovery: 0.48, action: "Track SCA volume by payor; renegotiate evergreen SCAs for high-volume out-of-network groups." },
  "Patient Responsibility":   { recovery: 0.55, action: "Move balances to patient AR workflow; surface accounts >$250 for outreach." },
  "Other":                    { recovery: 0.35, action: "Manually triage. Tag patterns that recur to add to the mapping library." },
};

// -----------------------------------------------------------------------------
// 4. Specialty refinement overlay — refines a group based on free-text context.
//    Fires only when domain-specific patterns appear (BH level-of-care, surgical
//    bundling, etc.). Does not restrict the engine to any one specialty.
// -----------------------------------------------------------------------------
const BH_REFINEMENTS = [
  // residential treatment + denial -> Level of Care typically
  { match: /residential/i,                  group: "Level of Care" },
  // PHP / IOP + auth -> Prior Authorization
  { match: /\b(php|iop|partial\s*hosp|intensive\s*outpatient)\b.*\bauth/i, group: "Prior Authorization" },
  // single-case agreement
  { match: /single\s*case\s*agreement|sca\b/i, group: "Single Case Agreement" },
  // benefit exhaustion patterns
  { match: /benefit.*exhaust|exhaust.*benefit/i, group: "Coverage Exhaustion" },
];

// -----------------------------------------------------------------------------
// 5. Public helpers
// -----------------------------------------------------------------------------
export const normalizeCode = (code) => {
  if (!code) return "";
  const c = String(code).trim().toUpperCase().replace(/\s+/g, "");
  // Tolerate "CO16" vs "CO-16"
  return c.replace(/^([A-Z]+)(\d)/, "$1-$2");
};

export const mapCodeToGroup = (code, freeText = "") => {
  const n = normalizeCode(code);
  if (RAW_CODE_MAP[n]) return RAW_CODE_MAP[n];
  // unknown numeric CARC -> "Other"
  if (freeText) {
    for (const [re, group] of KEYWORD_MAP) {
      if (re.test(freeText)) return group;
    }
  }
  return "Other";
};

// Given multiple codes + free text on a single denial line, infer the PRIMARY
// operational driver. Priority order (loosely): explicit auth > med necessity >
// eligibility > documentation > coding > policy > others.
const GROUP_PRIORITY = [
  "Prior Authorization",
  "Authorization",
  "Medical Necessity",
  "Level of Care",
  "Coordination of Benefits",
  "Eligibility",
  "Registration Errors",
  "Missing Documentation",
  "Invalid Modifiers",
  "Billing/Coding Errors",
  "Bundled Services",
  "Duplicate Claim",
  "Coverage Exhaustion",
  "Single Case Agreement",
  "Non-Covered Services",
  "Payor Policy Issues",
  "Timely Filing",
  "Filing Limits",
  "Patient Responsibility",
  "Other",
];

export const primaryDriver = ({ codes = [], description = "", remark = "" }) => {
  const text = `${description} ${remark}`.trim();
  const candidates = new Set();
  codes.forEach((c) => candidates.add(mapCodeToGroup(c, text)));
  if (candidates.size === 0) candidates.add(mapCodeToGroup("", text));

  // Apply specialty refinements if free text matches
  for (const ref of BH_REFINEMENTS) {
    if (ref.match.test(text)) {
      candidates.add(ref.group);
    }
  }

  // Pick highest priority candidate
  for (const g of GROUP_PRIORITY) if (candidates.has(g)) return g;
  return "Other";
};

// Aggregate an array of denials into Top N operational drivers.
//   denials: [{ codes: ["CO-197","M15"], description: "...", remark: "...", amount: 1234.56 }]
//   Returns: [{ group, count, dollars, pct, recovery, action, trend }]
export const aggregateDrivers = (denials, topN = 5) => {
  const buckets = new Map();
  let totalCount = 0;
  let totalDollars = 0;

  for (const d of denials) {
    const g = primaryDriver({
      codes: d.codes || [],
      description: d.description || "",
      remark: d.remark || "",
    });
    const amt = Number(d.amount) || 0;
    const prev = buckets.get(g) || { count: 0, dollars: 0, rawCodes: new Map() };
    prev.count += 1;
    prev.dollars += amt;
    // track raw codes for drill-down
    for (const c of d.codes || []) {
      const code = normalizeCode(c);
      prev.rawCodes.set(code, (prev.rawCodes.get(code) || 0) + 1);
    }
    buckets.set(g, prev);
    totalCount += 1;
    totalDollars += amt;
  }

  // Deterministic-looking trend for the preview: based on group hash.
  const trendFor = (g) => {
    const h = [...g].reduce((s, c) => s + c.charCodeAt(0), 0);
    const sign = h % 3 === 0 ? -1 : h % 3 === 1 ? 1 : 0;
    return sign;
  };

  const arr = [...buckets.entries()]
    .map(([group, v]) => {
      const meta = GROUP_META[group] || GROUP_META.Other;
      return {
        group,
        count: v.count,
        dollars: Math.round(v.dollars),
        pct: totalCount ? +(100 * v.count / totalCount).toFixed(1) : 0,
        dollarsPct: totalDollars ? +(100 * v.dollars / totalDollars).toFixed(1) : 0,
        recovery: meta.recovery,
        action: meta.action,
        trend: trendFor(group),
        rawCodes: [...v.rawCodes.entries()]
          .sort((a, b) => b[1] - a[1])
          .slice(0, 6)
          .map(([code, count]) => ({ code, count })),
      };
    })
    .sort((a, b) => b.dollars - a.dollars || b.count - a.count);

  return { totals: { totalCount, totalDollars }, drivers: arr.slice(0, topN), allDrivers: arr };
};

// -----------------------------------------------------------------------------
// 6. CSV row parser: try to figure out which columns hold what.
// -----------------------------------------------------------------------------
const splitCsvLine = (line) => {
  // Minimal CSV split that respects double-quoted fields.
  const out = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
};

const normalizeHeader = (h) =>
  String(h || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

export const parseDenialsCsv = (text) => {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return [];
  const headersRaw = splitCsvLine(lines[0]);
  const headers = headersRaw.map(normalizeHeader);

  const findIdx = (...candidates) => {
    for (const c of candidates) {
      const i = headers.findIndex((h) => h === c || h.includes(c));
      if (i !== -1) return i;
    }
    return -1;
  };

  // Best-effort column detection
  const idxCarc = findIdx("carc", "ansi_code", "denial_code", "reason_code", "adjustment_code", "code");
  const idxRarc = findIdx("rarc", "remark_code", "remark");
  const idxDesc = findIdx("description", "denial_reason", "reason", "denial_description");
  const idxAmt  = findIdx("billed_amount", "billed", "denied_amount", "amount", "charge_amount", "total_charges");

  const denials = [];
  for (let li = 1; li < lines.length; li++) {
    const cells = splitCsvLine(lines[li]);
    const codes = [];
    if (idxCarc !== -1 && cells[idxCarc]) {
      // codes can be like "CO-16;M15" or "CO16, N130"
      String(cells[idxCarc])
        .split(/[;,\s]+/)
        .filter(Boolean)
        .forEach((c) => codes.push(c));
    }
    if (idxRarc !== -1 && cells[idxRarc]) {
      String(cells[idxRarc])
        .split(/[;,\s]+/)
        .filter(Boolean)
        .forEach((c) => codes.push(c));
    }
    const description = idxDesc !== -1 ? cells[idxDesc] || "" : "";
    const remark = ""; // free-text remark same as description for our purposes
    const rawAmt = idxAmt !== -1 ? cells[idxAmt] : "";
    const amount = parseFloat(String(rawAmt || "0").replace(/[^0-9.\-]/g, "")) || 0;
    denials.push({ codes, description, remark, amount });
  }
  return denials;
};
